import { useState } from "react";
import { AlertTriangle, TrendingUp, DollarSign, Plus } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface BudgetAlertProps {
  availableBudget: number;
  pendingInvoicesTotal: number;
  blockedInvoicesCount: number;
  potentialSavings: number;
  currentBudget: number;
  onBudgetChange: (newBudget: number) => void;
}

export function BudgetAlert({
  availableBudget,
  pendingInvoicesTotal,
  blockedInvoicesCount,
  potentialSavings,
  currentBudget,
  onBudgetChange,
}: BudgetAlertProps) {
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [newBudget, setNewBudget] = useState(currentBudget);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCurrencyInput = (value: string) => {
    const num = parseInt(value.replace(/[^0-9]/g, "")) || 0;
    return num;
  };

  // Show alert if budget is low or there are blocked invoices
  const isLowBudget = availableBudget < pendingInvoicesTotal * 0.5;
  const hasBlockedInvoices = blockedInvoicesCount > 0;

  if (!isLowBudget && !hasBlockedInvoices) {
    return null;
  }

  // Calculate how much more budget is needed to cover all pending invoices
  const additionalBudgetNeeded = Math.max(0, pendingInvoicesTotal - availableBudget);
  const suggestedBudget = currentBudget + additionalBudgetNeeded;

  const handleOpenModal = () => {
    setNewBudget(currentBudget);
    setShowBudgetModal(true);
  };

  const handleSave = () => {
    onBudgetChange(newBudget);
    setShowBudgetModal(false);
  };

  const handleQuickAdd = (amount: number) => {
    setNewBudget((prev) => prev + amount);
  };

  const handleSetSuggested = () => {
    setNewBudget(suggestedBudget);
  };

  return (
    <>
      <Alert className="border-amber-500/50 bg-amber-500/10">
        <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        <AlertTitle className="text-foreground font-semibold flex items-center gap-2">
          {hasBlockedInvoices
            ? "Budget Insufficient for Pending Offers"
            : "Low Early Payment Budget"}
        </AlertTitle>
        <AlertDescription className="mt-2 space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2 text-foreground">
              <DollarSign className="w-4 h-4 text-muted-foreground" />
              <span>
                Available: <strong>{formatCurrency(availableBudget)}</strong>
              </span>
            </div>
            {hasBlockedInvoices && (
              <div className="flex items-center gap-2 text-foreground">
                <AlertTriangle className="w-4 h-4 text-muted-foreground" />
                <span>
                  <strong>{blockedInvoicesCount}</strong> invoice(s) waiting
                </span>
              </div>
            )}
          </div>

          {additionalBudgetNeeded > 0 && (
            <div className="flex items-center gap-2 text-sm bg-amber-500/20 rounded-md p-3 border border-amber-500/30">
              <DollarSign className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <span className="text-foreground">
                Add <strong>{formatCurrency(additionalBudgetNeeded)}</strong> to cover all pending
                invoices
              </span>
            </div>
          )}

          {potentialSavings > 0 && (
            <div className="flex items-center gap-2 text-sm bg-success/10 rounded-md p-3 border border-success/30">
              <TrendingUp className="w-4 h-4 text-success" />
              <span className="text-foreground">
                Potential savings you're missing:{" "}
                <strong>{formatCurrency(potentialSavings)}</strong>
              </span>
            </div>
          )}

          <p className="text-sm text-muted-foreground">
            {hasBlockedInvoices
              ? `There are ${blockedInvoicesCount} invoices that could be accelerated for early payment, but your current budget is insufficient. Increase your budget to maximize savings.`
              : "Your early payment budget is running low. Consider increasing it to continue capturing early payment discounts."}
          </p>

          <Button onClick={handleOpenModal} variant="default" className="mt-2">
            <DollarSign className="w-4 h-4 mr-2" />
            Increase Budget
          </Button>
        </AlertDescription>
      </Alert>

      {/* Budget Increase Modal */}
      <Dialog open={showBudgetModal} onOpenChange={setShowBudgetModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Increase Early Payment Budget</DialogTitle>
            <DialogDescription>
              Set your new early payment budget to unlock more discount offers.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newBudget">New Budget Amount</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">$</span>
                <Input
                  id="newBudget"
                  type="text"
                  value={newBudget.toLocaleString()}
                  onChange={(e) => setNewBudget(formatCurrencyInput(e.target.value))}
                  className="bg-background"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Current budget: {formatCurrency(currentBudget)}
              </p>
            </div>

            {additionalBudgetNeeded > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSetSuggested}
                className="w-full border-success/50 text-success hover:bg-success/10"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Set to {formatCurrency(suggestedBudget)} to cover all invoices
              </Button>
            )}

            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Quick Add</Label>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => handleQuickAdd(10000)}>
                  <Plus className="w-3 h-3 mr-1" />
                  $10K
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleQuickAdd(25000)}>
                  <Plus className="w-3 h-3 mr-1" />
                  $25K
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleQuickAdd(50000)}>
                  <Plus className="w-3 h-3 mr-1" />
                  $50K
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleQuickAdd(100000)}>
                  <Plus className="w-3 h-3 mr-1" />
                  $100K
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBudgetModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
